import React, { useState, useMemo, useRef, lazy, Suspense } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { CategoryIndicator } from './CategoryIndicator';
import { calculateScenarioImpact } from '@/lib/scenarioCalculator';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { PlusCircle, FolderOpen, Download, Info } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { COLORS } from '@/lib/colors';
import { cn } from '@/lib/utils';
import { Badge } from "@/components/ui/badge";
import { INITIATIVES as DATA_INITIATIVES } from '../data/initiatives';

// Lazy load ApexCharts
const ReactApexChart = lazy(() => import('react-apexcharts').then(module => ({ default: module.default })));

// Define custom categories that include the "Custom" option
const CATEGORIES = [
  "Outcomes",
  "Expert Opinion",
  "Student Excellence",
  "Faculty Research",
  "Infrastructure",
  "Technology",
  "Custom"
] as const;

type Category = typeof CATEGORIES[number];

// Define local Initiative interface
interface Initiative {
  id: string;
  name: string;
  category: Category;
  costRange: {
    min: number;
    max: number;
  };
  metrics: {
    name: string;
    weight: number;
    impact: number;
  }[];
  timeToImpact: "short" | "medium" | "long";
  difficulty: "low" | "medium" | "high";
  description: string;
  fullDescription?: string;
  interdependencies?: string[];
  caseStudies?: {
    university: string;
    outcome: string;
    link?: string;
  }[];
}

// Convert the imported initiatives to use our local type
const INITIATIVES: Initiative[] = DATA_INITIATIVES.map(initiative => ({
  ...initiative,
  category: initiative.category as Category
}));

// Update BASELINE_SCORES constant to match new categories
const BASELINE_SCORES = {
  "Outcomes": 70,
  "Expert Opinion": 68,
  "Student Excellence": 64,
  "Faculty Research": 72,
  "Infrastructure": 60,
  "Technology": 58
};

// Add this interface near the top of the file where other interfaces are defined
interface InitiativeDetailsProps {
  initiative: Initiative;
  onClose: () => void;
}

// Format currency function to handle both K and M notation
const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else {
    return `$${(value / 1000).toFixed(0)}K`;
  }
};

const InitiativeDetails: React.FC<InitiativeDetailsProps> = ({ initiative, onClose }) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{initiative.name}</h2>
        <Badge variant="outline" className="text-sm">
          {initiative.category}
        </Badge>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-500">Cost Range</div>
          <div className="font-semibold">{formatCurrency(initiative.costRange.min)} - {formatCurrency(initiative.costRange.max)}</div>
        </div>
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-500">Time to Impact</div>
          <div className="font-semibold capitalize">{initiative.timeToImpact || "Medium"}</div>
        </div>
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-500">Implementation Difficulty</div>
          <div className="font-semibold capitalize">{initiative.difficulty || "Medium"}</div>
        </div>
      </div>
      
      <div className="space-y-2">
        <div className="text-sm font-medium text-gray-500">Description</div>
        <p className="text-gray-900">{initiative.fullDescription || initiative.description}</p>
      </div>
      
      <div className="space-y-3">
        <div className="text-sm font-medium text-gray-500">Impact Metrics</div>
        <div className="grid grid-cols-2 gap-4">
          {initiative.metrics.map((metric, idx) => (
            <div key={idx} className="p-3 bg-blue-50 rounded-md">
              <div className="text-sm font-medium">{metric.name}</div>
              <div className="mt-1 flex items-center gap-2">
                <div className="h-2 w-full bg-gray-200 rounded-full">
                  <div
                    className="h-full bg-blue-600 rounded-full"
                    style={{ width: `${metric.impact * 10}%` }}
                  />
                </div>
                <span className="text-sm font-semibold">{metric.impact}/10</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {initiative.interdependencies && initiative.interdependencies.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-500">Related To</div>
          <div className="flex flex-wrap gap-2">
            {initiative.interdependencies.map((dep, idx) => (
              <Badge key={idx} variant="secondary">{dep}</Badge>
            ))}
          </div>
        </div>
      )}
      
      {initiative.caseStudies && initiative.caseStudies.length > 0 && (
        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-500">Success Stories</div>
          <div className="space-y-3">
            {initiative.caseStudies.map((study, idx) => (
              <div key={idx} className="p-3 bg-gray-50 rounded-md">
                <div className="font-medium">{study.university}</div>
                <div className="text-sm text-gray-700 mt-1">{study.outcome}</div>
                {study.link && (
                  <a 
                    href={study.link} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 mt-2 inline-block hover:underline"
                  >
                    Learn more →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="pt-4 flex justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
};

// Define ScenarioImpact interface locally
interface ScenarioImpact {
  projectedRank: number;
  rankChange: number;
  categoryContributions: Record<string, number>;
  confidenceInterval: {
    lower: number;
    upper: number;
  };
}

// Fix the getTopContributingFactors function
const getTopContributingFactors = (scenarioImpact: ScenarioImpact, initiatives: Initiative[]) => {
  // Get the top 3 categories by contribution
  const sortedCategories = Object.entries(scenarioImpact.categoryContributions)
    .map(([category, value]) => ({ category, value: value as number }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 3);
  
  // Get the top initiatives by impact for each of these categories
  return sortedCategories.map(({ category }) => {
    const topInitiatives = initiatives
      .filter(initiative => 
        initiative.metrics.some(metric => 
          metric.name.toLowerCase().includes(category.toLowerCase())
        )
      )
      .slice(0, 2);
    
    return {
      category,
      initiatives: topInitiatives
    };
  });
};

// First, add a constant for the Custom category
const CUSTOM_CATEGORY = "Custom";

// First, define a local extension of the Initiative interface that supports the Custom category
interface LocalInitiative extends Omit<Initiative, 'category'> {
  category: typeof CATEGORIES[number] | typeof CUSTOM_CATEGORY;
}

// Define interface for component props
interface ScenarioBuilderProps {
  currentRank: number;
}

// Define a constant for empty string
const EMPTY_CATEGORY = "" as const;
type CategoryState = Category | typeof EMPTY_CATEGORY;

export const ScenarioBuilder: React.FC<ScenarioBuilderProps> = ({ currentRank }) => {
  const [selectedCategory, setSelectedCategory] = useState<CategoryState>(EMPTY_CATEGORY);
  const [budget, setBudget] = useState<number>(1000000);
  const [budgetInput, setBudgetInput] = useState<string>("1.0");
  const [selectedInitiatives, setSelectedInitiatives] = useState<LocalInitiative[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [customInitiative, setCustomInitiative] = useState<Partial<Initiative>>({
    category: EMPTY_CATEGORY as any, // Cast to any to avoid type error
    costRange: { min: 0, max: 0 },
    metrics: [{ name: "Custom Impact", weight: 5, impact: 0.5 }],
    timeToImpact: "medium" as const,
    difficulty: "medium" as const
  });
  const [editingInitiative, setEditingInitiative] = useState<Initiative | null>(null);
  const scenarioRef = useRef<HTMLDivElement>(null);
  const [viewingInitiative, setViewingInitiative] = useState<Initiative | null>(null);

  // Calculate total budget used
  const totalBudgetUsed = useMemo(() => {
    return selectedInitiatives.reduce((acc, initiative) => 
      acc + (initiative.costRange.min + initiative.costRange.max) / 2, 0
    );
  }, [selectedInitiatives]);

  // Check if budget would be exceeded
  const wouldExceedBudget = (initiative: Initiative) => {
    const initiativeCost = (initiative.costRange.min + initiative.costRange.max) / 2;
    return totalBudgetUsed + initiativeCost > budget;
  };

  // Calculate impact
  const scenarioImpact = useMemo(() => {
    return calculateScenarioImpact(selectedInitiatives, currentRank);
  }, [selectedInitiatives, currentRank]);

  // Handle adding initiative
  const handleAddInitiative = (initiative: Initiative) => {
    if (wouldExceedBudget(initiative)) {
      alert("Adding this initiative would exceed your budget.");
      return;
    }
    setSelectedInitiatives([...selectedInitiatives, initiative as LocalInitiative]);
  };

  // Handle removing initiative
  const handleRemoveInitiative = (initiativeId: string) => {
    setSelectedInitiatives(selectedInitiatives.filter(i => i.id !== initiativeId));
  };

  // Handle step navigation
  const handleNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBudgetChange = (value: string) => {
    // Update the input field value
    setBudgetInput(value);
    
    // Convert to cents to avoid floating point issues
    const numValue = Math.round(parseFloat(value) * 1000000);
    if (!isNaN(numValue) && numValue > 0) {
      setBudget(numValue);
    }
  };

  const handleReset = () => {
    setBudget(1000000);
    setBudgetInput("1.0");
    setSelectedCategory(EMPTY_CATEGORY);
    setSelectedInitiatives([]);
    setCurrentStep(1);
    setCustomInitiative({
      category: EMPTY_CATEGORY as any, // Cast to any to avoid type error
      costRange: { min: 0, max: 0 },
      metrics: [{ name: "Custom Impact", weight: 5, impact: 0.5 }],
      name: "",
      description: "",
      timeToImpact: "medium" as const,
      difficulty: "medium" as const
    });
  };

  const handleAddCustomInitiative = (newInitiative: Initiative) => {
    setSelectedInitiatives([...selectedInitiatives, newInitiative as LocalInitiative]);
  };

  const resetCustomInitiativeForm = () => {
    setCustomInitiative({
      category: EMPTY_CATEGORY as any, // Cast to any to avoid type error
      costRange: { min: 0, max: 0 },
      metrics: [{ name: "Custom Impact", weight: 5, impact: 0.5 }],
      name: "",
      description: "",
      timeToImpact: "medium" as const,
      difficulty: "medium" as const
    });
  };

  const handleSaveInitiative = (updatedInitiative: Initiative) => {
    setSelectedInitiatives(selectedInitiatives.map(initiative => 
      initiative.id === updatedInitiative.id ? updatedInitiative : initiative
    ));
    setEditingInitiative(null);
  };

  const exportToPDF = async () => {
    if (!scenarioRef.current) return;

    try {
      const canvas = await html2canvas(scenarioRef.current, {
        scale: 2,
        logging: false,
        useCORS: true
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');

      // Add title
      pdf.setFontSize(20);
      pdf.text('DePaul University Ranking Scenario', 105, 15, { align: 'center' });
      
      // Add date
      pdf.setFontSize(12);
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, 105, 25, { align: 'center' });

      // Add scenario content
      pdf.addImage(imgData, 'PNG', 0, 35, imgWidth, imgHeight);

      // Add summary section
      const y = imgHeight + 45;
      pdf.setFontSize(14);
      pdf.text('Scenario Summary', 15, y);
      pdf.setFontSize(12);
      pdf.text(`Total Budget: $${budget.toLocaleString()}`, 15, y + 10);
      pdf.text(`Selected Initiatives: ${selectedInitiatives.length}`, 15, y + 20);
      pdf.text(`Projected Rank Change: ${currentRank - scenarioImpact.projectedRank}`, 15, y + 30);

      pdf.save('depaul-ranking-scenario.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  // Step-specific content
  const renderStepContent = () => {
    switch(currentStep) {
      case 1:
        return (
          <div className="space-y-6 pt-6">
            {/* Budget Input */}
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold">Set Your Budget</h2>
                <p className="text-gray-500">Enter the total budget available for your initiatives</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="budget">Budget Amount (in millions)</Label>
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 max-w-xs">
                    <span className="absolute left-3 top-2.5">$</span>
                    <Input
                      id="budget"
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={budgetInput}
                      onChange={(e) => handleBudgetChange(e.target.value)}
                      className="pl-7"
                      placeholder="1.0"
                    />
                  </div>
                  <Button
                    onClick={handleNextStep}
                    disabled={budget <= 0}
                    className="bg-[#274791] hover:bg-[#274791]/90 text-white"
                  >
                    Enter
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8 pt-6">
            {/* Category Selection */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Select Initiatives</h2>
              <p className="text-gray-500">Choose initiatives to improve your ranking</p>
              <div className="grid grid-cols-3 gap-4">
                {CATEGORIES.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    className={`
                      h-auto py-4 px-6 
                      flex flex-col items-center gap-2
                      transition-all duration-200
                      ${selectedCategory === category ? 'bg-[#274791] text-white scale-105' : 'hover:bg-[#274791]/5'}
                    `}
                    onClick={() => setSelectedCategory(category)}
                  >
                    <span className="text-base font-medium">{category}</span>
                    {selectedCategory === category && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-xs opacity-80"
                      >
                        {INITIATIVES.filter(i => i.category === category).length} initiatives
                      </motion.div>
                    )}
                  </Button>
                ))}
              </div>
            </div>

            {/* Initiative List */}
            <AnimatePresence mode="wait">
              <motion.div 
                key={selectedCategory}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="w-full"
              >
                {selectedCategory === CUSTOM_CATEGORY ? (
                  <Dialog onOpenChange={(open) => {
                    if (open) {
                      resetCustomInitiativeForm();
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full h-[120px] border-dashed">
                        <div className="flex flex-col items-center gap-2">
                          <PlusCircle className="h-6 w-6" />
                          <span>Create New Initiative</span>
                        </div>
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Custom Initiative</DialogTitle>
                        <DialogDescription>
                          Define your own initiative with custom parameters
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="name">Initiative Name</Label>
                          <Input
                            id="name"
                            value={customInitiative.name || ""}
                            onChange={(e) => setCustomInitiative({
                              ...customInitiative,
                              name: e.target.value
                            })}
                          />
                        </div>
                        <div>
                          <Label>Cost Range</Label>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-2.5">$</span>
                              <Input
                                type="number"
                                placeholder="Min"
                                className="pl-7"
                                value={customInitiative.costRange?.min || ""}
                                onChange={(e) => setCustomInitiative({
                                  ...customInitiative,
                                  costRange: {
                                    ...customInitiative.costRange,
                                    min: parseInt(e.target.value)
                                  }
                                })}
                              />
                            </div>
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-2.5">$</span>
                              <Input
                                type="number"
                                placeholder="Max"
                                className="pl-7"
                                value={customInitiative.costRange?.max || ""}
                                onChange={(e) => setCustomInitiative({
                                  ...customInitiative,
                                  costRange: {
                                    ...customInitiative.costRange,
                                    max: parseInt(e.target.value)
                                  }
                                })}
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="description">Description</Label>
                          <textarea
                            id="description"
                            className="w-full min-h-[100px] p-2 border rounded"
                            value={customInitiative.description || ""}
                            onChange={(e) => setCustomInitiative({
                              ...customInitiative,
                              description: e.target.value
                            })}
                          />
                        </div>
                        <Button
                          onClick={() => {
                            if (customInitiative.name && customInitiative.description) {
                              handleAddCustomInitiative({
                                id: `custom-${Date.now()}`,
                                name: customInitiative.name,
                                category: CUSTOM_CATEGORY,
                                costRange: customInitiative.costRange!,
                                description: customInitiative.description,
                                metrics: [{ name: "Custom Impact", weight: 5, impact: 0.5 }],
                                timeToImpact: "medium",
                                difficulty: "medium"
                              });
                              resetCustomInitiativeForm(); // Reset form after adding
                            }
                          }}
                        >
                          Add Initiative
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : selectedCategory ? (
                  <div className="w-full">
                    {/* Header */}
                    <div className="grid grid-cols-[1fr_100px_100px] gap-4 px-6 py-3 bg-gray-50 rounded-t-lg border-b">
                      <div className="text-sm font-semibold text-gray-700">Initiative</div>
                      <div className="text-sm font-semibold text-gray-700 text-center">Cost</div>
                      <div className="text-sm font-semibold text-gray-700 text-center">Action</div>
                    </div>

                    {/* Initiatives List */}
                    <div className="divide-y">
                      {INITIATIVES
                        .filter(i => i.category === selectedCategory)
                        .map((initiative) => {
                          const isSelected = selectedInitiatives.some(i => i.id === initiative.id);
                          const exceeds = !isSelected && wouldExceedBudget(initiative);
                          
                          return (
                            <div 
                              key={initiative.id}
                              className={cn(
                                "grid grid-cols-[1fr_120px_180px] gap-4 px-6 py-4 hover:bg-gray-50 transition-colors",
                                isSelected && "bg-blue-50/50"
                              )}
                            >
                              {/* Initiative Info */}
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                <h3 className="font-medium text-gray-900">{initiative.name}</h3>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0 rounded-full"
                                    onClick={() => setViewingInitiative(initiative)}
                                  >
                                    <Info className="h-4 w-4" />
                                  </Button>
                                </div>
                                <p className="text-sm text-gray-500 line-clamp-2">{initiative.description}</p>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {initiative.metrics.slice(0, 2).map((metric, idx) => (
                                    <div key={idx} className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                                      {metric.name}: {metric.impact}/10
                                    </div>
                                  ))}
                                  {initiative.timeToImpact && (
                                    <div className={cn(
                                      "inline-flex items-center text-xs px-2 py-1 rounded-full",
                                      initiative.timeToImpact === "short" ? "bg-green-50 text-green-700" :
                                      initiative.timeToImpact === "medium" ? "bg-yellow-50 text-yellow-700" :
                                      "bg-orange-50 text-orange-700"
                                    )}>
                                      {initiative.timeToImpact} term
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Cost */}
                              <div className="flex items-center">
                                <div className="text-sm">
                                  <div className="font-medium text-gray-900">
                                    {formatCurrency(initiative.costRange.min)} - {formatCurrency(initiative.costRange.max)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Est. avg: {formatCurrency((initiative.costRange.min + initiative.costRange.max) / 2)}
                                  </div>
                                </div>
                              </div>

                              {/* Action Button */}
                              <div className="flex items-center justify-end">
                                <Button
                                  onClick={() => isSelected ? handleRemoveInitiative(initiative.id) : handleAddInitiative(initiative)}
                                  disabled={exceeds}
                                  className={cn(
                                    "w-24 h-9 text-sm",
                                    isSelected
                                      ? "bg-red-100 text-red-700 hover:bg-red-200"
                                      : exceeds
                                      ? "bg-gray-100 text-gray-500"
                                      : "bg-[#274791] text-white hover:bg-[#274791]/90"
                                  )}
                                >
                                  {isSelected ? "Remove" : exceeds ? "Exceeds" : "Add"}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <FolderOpen className="h-12 w-12 mb-4 opacity-50" />
                    <p className="text-lg font-medium">Select a category to view initiatives</p>
                    <p className="text-sm mt-1">Choose from the categories above to explore available options</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8 pt-8">
            {/* Budget Distribution */}
            <div>
              <h2 className="text-lg font-semibold mb-6">Budget Distribution</h2>
              <div className="h-[250px]">
                <Suspense fallback={<div className="h-[250px] flex items-center justify-center">Loading chart...</div>}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'pie',
                        fontFamily: 'inherit',
                      },
                      labels: selectedInitiatives.reduce((acc, initiative) => {
                        const category = initiative.category;
                        if (!acc.includes(category)) acc.push(category);
                        return acc;
                      }, [] as string[]),
                      colors: [
                        '#0020B5', // Outcomes
                        '#923CBC', // Expert Opinion
                        '#FAC6D2', // Faculty Resources
                        '#FFB600', // Financial Resources
                        '#0A5A44', // Student Excellence
                        '#003DA5', // Faculty Research
                        '#55251F'  // Custom
                      ],
                      legend: {
                        position: 'bottom',
                        fontSize: '14px'
                      },
                      dataLabels: {
                        formatter: function(val: number, opts) {
                          const value = opts.w.globals.seriesTotals[opts.seriesIndex];
                          return `$${(value/1000000).toFixed(1)}M`;
                        }
                      }
                    }}
                    series={selectedInitiatives.reduce((acc, initiative) => {
                      const category = initiative.category;
                      const cost = (initiative.costRange.min + initiative.costRange.max) / 2;
                      const categoryIndex = acc.findIndex(item => item.category === category);
                      
                      if (categoryIndex >= 0) {
                        acc[categoryIndex].value += cost;
                      } else {
                        acc.push({
                          category,
                          value: cost
                        });
                      }
                      return acc;
                    }, [] as { category: string; value: number }[]).map(item => item.value)}
                    type="pie"
                    height={250}
                  />
                </Suspense>
              </div>
            </div>

            {/* Category Impact Analysis */}
            <div className="pt-4">
              <h2 className="text-lg font-semibold mb-6">Category Impact Analysis</h2>
              <div className="h-[350px]">
                <Suspense fallback={<div className="h-[350px] flex items-center justify-center">Loading chart...</div>}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'radar',
                        fontFamily: 'inherit',
                        toolbar: {
                          show: false
                        }
                      },
                      xaxis: {
                        categories: Object.keys(scenarioImpact.categoryContributions)
                      },
                      yaxis: {
                        show: true,
                        min: 0,
                        max: 100,
                        tickAmount: 5
                      },
                      colors: ['#274791', '#FF9E1B'],
                      stroke: {
                        width: 2
                      },
                      fill: {
                        opacity: 0.25
                      },
                      markers: {
                        size: 4
                      },
                      legend: {
                        position: 'bottom'
                      }
                    }}
                    series={[
                      {
                        name: 'Current',
                        data: Object.entries(BASELINE_SCORES).map(([_, value]) => value)
                      },
                      {
                        name: 'Impact',
                        data: Object.values(scenarioImpact.categoryContributions).map(value => Math.max(0, value * 100))
                      }
                    ]}
                    type="radar"
                    height={350}
                  />
                </Suspense>
              </div>
            </div>

            {/* Impact Breakdown by Initiative */}
            <div className="pt-4">
              <h2 className="text-lg font-semibold mb-6">Impact Breakdown by Initiative</h2>
              <div className="h-[300px]">
                <Suspense fallback={<div className="h-[300px] flex items-center justify-center">Loading chart...</div>}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        fontFamily: 'inherit',
                        toolbar: {
                          show: false
                        },
                        stacked: true
                      },
                      plotOptions: {
                        bar: {
                          horizontal: true,
                          barHeight: '70%',
                          distributed: false
                        }
                      },
                      dataLabels: {
                        enabled: false
                      },
                      xaxis: {
                        categories: selectedInitiatives.map(i => i.name),
                        labels: {
                          formatter: function(val) {
                            return val.toFixed(2);
                          }
                        }
                      },
                      legend: {
                        position: 'bottom'
                      },
                      tooltip: {
                        y: {
                          formatter: function(val) {
                            return val.toFixed(2) + ' impact';
                          }
                        }
                      },
                      colors: ['#0020B5', '#923CBC', '#FAC6D2', '#FFB600', '#0A5A44', '#003DA5']
                    }}
                    series={
                      selectedInitiatives.length > 0 
                      ? selectedInitiatives[0].metrics.map((m, i) => ({
                          name: m.name,
                          data: selectedInitiatives.map(initiative => {
                            const metric = initiative.metrics.find(metric => metric.name === m.name);
                            return metric ? metric.impact : 0;
                          })
                        }))
                      : []
                    }
                    type="bar"
                    height={300}
                  />
                </Suspense>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <Card className="border-0 shadow-none">
              <CardHeader>
                <CardTitle>Strategy Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium mb-2">Selected Initiatives</h3>
                    <div className="space-y-2">
                      {selectedInitiatives.map((initiative) => (
                        <div
                          key={initiative.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                        >
                          <div>
                            <span className="font-medium">{initiative.name}</span>
                            <div className="text-sm text-gray-500">
                              Category: {initiative.category}
                            </div>
                            <div className="text-sm text-gray-500">
                              Cost: {formatCurrency((initiative.costRange.min + initiative.costRange.max) / 2)}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveInitiative(initiative.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium mb-2">Final Impact</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-md">
                        <div className="text-sm text-gray-500">Current Rank</div>
                        <div className="text-2xl font-bold">{currentRank}</div>
                      </div>
                      <motion.div 
                        className={`p-4 rounded-md ${
                          selectedInitiatives.length === 0 
                            ? 'bg-gray-50' 
                            : scenarioImpact.projectedRank < currentRank 
                              ? 'bg-green-50' 
                              : 'bg-red-50'
                        }`}
                        initial={{ scale: 1 }}
                        animate={{ 
                          scale: [1, 1.05, 1],
                          transition: { duration: 0.3 }
                        }}
                        key={scenarioImpact.projectedRank}
                      >
                        <div className="text-sm text-gray-500">Projected Rank</div>
                        <div className="flex items-center gap-2">
                          <motion.div 
                            className="text-2xl font-bold"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            key={scenarioImpact.projectedRank}
                          >
                            {scenarioImpact.projectedRank}
                          </motion.div>
                          <AnimatePresence>
                            {selectedInitiatives.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                className={`text-sm ${
                                  scenarioImpact.projectedRank < currentRank 
                                    ? 'text-green-600' 
                                    : 'text-red-600'
                                }`}
                              >
                                {scenarioImpact.projectedRank < currentRank ? '↑' : '↓'} 
                                {Math.abs(currentRank - scenarioImpact.projectedRank)} positions
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        {selectedInitiatives.length === 0 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-sm text-gray-500 mt-1"
                          >
                            Baseline (No Initiatives)
                          </motion.div>
                        )}
                      </motion.div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-[#274791]/20 p-4 mb-6">
                    <h3 className="text-lg font-semibold text-[#274791] mb-2">Budget Allocation</h3>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-[#FCE58E] font-bold">Used: ${(totalBudgetUsed / 1000000).toFixed(1)}M</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#274791] font-bold">Remaining: ${((budget - totalBudgetUsed) / 1000000).toFixed(1)}M</span>
                      </div>
                    </div>
                    <div className="mt-2 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#274791]"
                        style={{ width: `${Math.min(100, (totalBudgetUsed / budget) * 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* New section - Ranking Explanation */}
                  {selectedInitiatives.length > 0 && (
                    <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className="text-lg font-semibold text-[#274791] mb-3">Ranking Impact Analysis</h3>
                      
                      <div className="space-y-4">
                        <p className="text-sm text-gray-700">
                          Your selected initiatives are projected to {scenarioImpact.projectedRank < currentRank ? 'improve' : 'affect'} your ranking 
                          by <span className="font-semibold">{Math.abs(currentRank - scenarioImpact.projectedRank)} positions</span>, 
                          with a confidence interval of {scenarioImpact.confidenceInterval.lower} to {scenarioImpact.confidenceInterval.upper}.
                        </p>
                        
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-gray-700">Key Impact Factors:</div>
                          <ul className="space-y-2">
                            {getTopContributingFactors(scenarioImpact, selectedInitiatives).map((factor, idx) => (
                              <li key={idx} className="text-sm bg-white p-3 rounded border border-gray-100">
                                <span className="font-medium">{factor.category}:</span>{' '}
                                {factor.initiatives.length > 0 ? (
                                  <>
                                    Driven by{' '}
                                    {factor.initiatives.map((initiative, i) => (
                                      <span key={i}>
                                        <span className="text-blue-600">{initiative.name}</span>
                                        {i < factor.initiatives.length - 1 ? ' and ' : ''}
                                      </span>
                                    ))}
                                  </>
                                ) : (
                                  'No specific initiatives found for this category'
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="mt-2 text-sm text-gray-500">
                          <p className="font-medium mb-1">How Rankings Are Calculated:</p>
                          <p>
                            Rankings are calculated based on weighted contributions from each category. 
                            Initiatives have diminishing returns when multiple are applied to the same category. 
                            For optimal results, consider a balanced approach across different categories.
                          </p>
                        </div>
                        
                        <div className="mt-2 text-sm">
                          <p className="font-medium text-amber-700">Implementation Timeline:</p>
                          <p className="text-gray-700">
                            {selectedInitiatives.some(i => i.timeToImpact === "short") ? 
                              "Some initiatives can show results within 1 year, while " : 
                              "Most initiatives require at least "
                            }
                            {selectedInitiatives.some(i => i.timeToImpact === "long") ? 
                              "others may take 3+ years for full impact." : 
                              "1-2 years for meaningful impact."
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 space-y-4">
                    <h3 className="font-medium">Export and Share</h3>
                    <Button
                      onClick={exportToPDF}
                      className="w-full flex items-center justify-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Export as PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div ref={scenarioRef} className="space-y-6 p-6 bg-white rounded-lg">
              <h3 className="text-2xl font-bold">Scenario Results</h3>
              
              {/* Summary Section */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold">Budget Utilization</h4>
                  <p className="text-2xl font-bold">{totalBudgetUsed.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">of ${budget.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold">Initiatives</h4>
                  <p className="text-2xl font-bold">{selectedInitiatives.length}</p>
                  <p className="text-sm text-gray-600">Selected</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold">Rank Change</h4>
                  <p className="text-2xl font-bold">{currentRank - scenarioImpact.projectedRank}</p>
                  <p className="text-sm text-gray-600">Positions</p>
                </div>
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h4 className="font-semibold">Category Impact</h4>
                  <div className="h-[300px]">
                    <Suspense fallback={<div className="h-[300px] flex items-center justify-center">Loading chart...</div>}>
                      <ReactApexChart
                        options={{
                          chart: {
                            type: 'radar',
                            fontFamily: 'inherit',
                            toolbar: {
                              show: false
                            }
                          },
                          xaxis: {
                            categories: Object.keys(scenarioImpact.categoryContributions)
                          },
                          yaxis: {
                            show: true,
                            min: 0,
                            max: 100,
                            tickAmount: 5
                          },
                          colors: ['#274791', '#FF9E1B'],
                          stroke: {
                            width: 2
                          },
                          fill: {
                            opacity: 0.25
                          },
                          markers: {
                            size: 4
                          },
                          legend: {
                            position: 'bottom'
                          }
                        }}
                        series={[
                          {
                            name: 'Current',
                            data: Object.entries(BASELINE_SCORES).map(([_, value]) => value)
                          },
                          {
                            name: 'Impact',
                            data: Object.values(scenarioImpact.categoryContributions).map(value => Math.max(0, value * 100))
                          }
                        ]}
                        type="radar"
                        height={300}
                      />
                    </Suspense>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold">Cost Distribution</h4>
                  <div className="h-[300px]">
                    <Suspense fallback={<div className="h-[300px] flex items-center justify-center">Loading chart...</div>}>
                      <ReactApexChart
                        options={{
                          chart: {
                            type: 'pie',
                            fontFamily: 'inherit',
                          },
                          labels: selectedInitiatives.reduce((acc, initiative) => {
                            const category = initiative.category;
                            if (!acc.includes(category)) acc.push(category);
                            return acc;
                          }, [] as string[]),
                          colors: [
                            '#0020B5', // Outcomes
                            '#923CBC', // Expert Opinion
                            '#FAC6D2', // Faculty Resources
                            '#FFB600', // Financial Resources
                            '#0A5A44', // Student Excellence
                            '#003DA5', // Faculty Research
                            '#55251F'  // Custom
                          ],
                          legend: {
                            position: 'bottom',
                            fontSize: '14px'
                          },
                          dataLabels: {
                            formatter: function(val: number, opts) {
                              const value = opts.w.globals.seriesTotals[opts.seriesIndex];
                              return `$${(value/1000000).toFixed(1)}M`;
                            }
                          }
                        }}
                        series={selectedInitiatives.reduce((acc, initiative) => {
                          const category = initiative.category;
                          const cost = (initiative.costRange.min + initiative.costRange.max) / 2;
                          const categoryIndex = acc.findIndex(item => item.category === category);
                          
                          if (categoryIndex >= 0) {
                            acc[categoryIndex].value += cost;
                          } else {
                            acc.push({
                              category,
                              value: cost
                            });
                          }
                          return acc;
                        }, [] as { category: string; value: number }[]).map(item => item.value)}
                        type="pie"
                        height={300}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>

              {/* Selected Initiatives */}
              <div className="mt-6">
                <h4 className="font-semibold mb-4">Selected Initiatives</h4>
                <div className="space-y-2">
                  {selectedInitiatives.map((initiative) => (
                    <div key={initiative.id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{initiative.name}</p>
                          <p className="text-sm text-gray-600">{initiative.category}</p>
                        </div>
                        <p className="font-medium">{formatCurrency(initiative.costRange.min)} - {formatCurrency(initiative.costRange.max)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Fixed header with progress steps */}
      <div className="sticky top-0 bg-white z-10 py-3 border-b shadow-sm">
        <div className="container mx-auto max-w-screen-xl px-4">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <div className="flex items-center max-w-3xl">
                {[
                  "Set Budget",
                  "Select Initiatives",
                  "Review Impact",
                  "Finalize Strategy"
                ].map((stepLabel, index) => (
                  <div key={index} className="flex items-center flex-1">
                    <div 
                      className={`
                        w-7 h-7 rounded-full flex items-center justify-center text-sm
                        ${currentStep === index + 1 ? `bg-[${COLORS.primary.darkBlue}] text-white` : 
                          currentStep > index + 1 ? `bg-[${COLORS.primary.darkBlue}] text-white` : 'bg-gray-200'}
                      `}
                    >
                      {index + 1}
                    </div>
                    <span className="ml-2 text-xs font-medium whitespace-nowrap">{stepLabel}</span>
                    {index < 3 && (
                      <div className={`flex-1 h-0.5 mx-2 ${
                        currentStep > index + 1 ? `bg-[${COLORS.primary.darkBlue}]` : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleReset}
              className="ml-6 text-sm"
              size="sm"
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 py-4">
        <div className="container mx-auto max-w-screen-xl px-4">
          <div className="grid grid-cols-12 gap-6">
            {/* Left Column - Scrollable content */}
            <div className="col-span-8">
              <Card className="min-h-[calc(100vh-8rem)] bg-white">
                <CardContent className="h-full p-6">
                  <div className="max-w-3xl mx-auto">
                    {renderStepContent()}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Sticky sidebar */}
            <div className="col-span-4">
              <div className="space-y-4 sticky top-[4.5rem]">
                {/* Budget Card */}
                <Card className="bg-white">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm font-medium">Budget Allocation</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4">
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg border border-[#274791]/20 p-4 mb-6">
                        <h3 className="text-lg font-semibold text-[#274791] mb-2">Budget Allocation</h3>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="text-[#FCE58E] font-bold">Used: ${(totalBudgetUsed / 1000000).toFixed(1)}M</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[#274791] font-bold">Remaining: ${((budget - totalBudgetUsed) / 1000000).toFixed(1)}M</span>
                          </div>
                        </div>
                        <div className="mt-2 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#274791]"
                            style={{ width: `${Math.min(100, (totalBudgetUsed / budget) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Selected Initiatives - Scrollable */}
                <Card className="bg-white">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm font-medium">Selected Initiatives</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 max-h-[calc(100vh-24rem)] overflow-y-auto">
                    <div className="space-y-2">
                      {selectedInitiatives.map((initiative) => (
                        <div
                          key={initiative.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <span className="text-sm font-medium">{initiative.name}</span>
                            <div className="text-xs text-gray-500">
                              {formatCurrency(initiative.costRange.min)} - {formatCurrency(initiative.costRange.max)}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveInitiative(initiative.id)}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      {selectedInitiatives.length === 0 && (
                        <div className="text-sm text-gray-500 text-center py-4">
                          No initiatives selected
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-6">
                  <Button
                    variant="outline"
                    onClick={handlePrevStep}
                    disabled={currentStep === 1}
                    className="border-[#274791] text-[#274791] hover:bg-[#274791]/10"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={handleNextStep}
                    disabled={currentStep === 2 && selectedInitiatives.length === 0}
                    className="bg-[#274791] text-white hover:bg-[#274791]/90"
                  >
                    {currentStep === 4 ? 'Finish' : 'Next'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Initiative Details Dialog */}
      <Dialog open={!!viewingInitiative} onOpenChange={(open) => {
        if (!open) setViewingInitiative(null);
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {viewingInitiative && (
            <InitiativeDetails
              initiative={viewingInitiative}
              onClose={() => setViewingInitiative(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}; 